// Inline citations ([1], [2], ...) -> hover-preview source cards.
//
// markstream renders bare numeric brackets ([1]) as `reference` nodes. We
// override that node with CitationChip.vue and feed it the message's sources so
// hovering a [n] chip previews the nth source - the Perplexity affordance.
//
// There is NO backend "sources" contract: the sources are ALREADY on the
// assistant bubble as tool results (bubble.tools[].outputFull). This flattens
// the SAME data the Sources/Workspace/Memory viewparts parse into one ordered
// list, so [n] = the nth source the agent surfaced (in tool-call order). The
// parse logic mirrors components/toolRenderers/* exactly so a citation card and
// the fold below it never disagree. Keep them in sync.
import type { InjectionKey, ComputedRef } from 'vue';
import type { ToolStep } from '../types';

export type CitationKind = 'web' | 'workspace' | 'memory';

export interface Citation {
  kind:     CitationKind;
  title:    string;
  snippet:  string;
  url?:     string;   // web only - http(s), opens in a new tab
  host?:    string;   // web only - the bare hostname
  meta?:    string;   // workspace/memory provenance line (#channel, author, date)
}

// Reactive per-message citation list, provided by CitationProvider and injected
// by CitationChip. Default is an empty list (static/doc renders with no message
// context), so a stray [1] there degrades to a neutral marker.
export const CITATION_KEY: InjectionKey<ComputedRef<Citation[]>> = Symbol('hqCitations');

// Mirror ToolViewparts.baseName: the same tool arrives bare (`search_web`) on
// the fast lane and SDK-prefixed (`mcp__hq__search_web`) on the agent path.
function baseName(name: string): string {
  return name.startsWith('mcp__') ? name.split('__').slice(2).join('__') : name;
}

function isHttp(url: string): boolean {
  return /^https?:\/\//i.test(url);
}
function hostOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return url; }
}

function parseJson(raw: string | undefined): unknown {
  if (!raw) return null;
  try { return JSON.parse(raw); }
  catch { return null; }
}

function asArray(data: unknown, key: string): unknown[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const v = (data as Record<string, unknown>)[key];
    if (Array.isArray(v)) return v;
  }
  return [];
}

// search_web -> {title,url,snippet}, wrapped bare or under `results`.
function webCitations(tool: ToolStep): Citation[] {
  const arr = asArray(parseJson(tool.outputFull), 'results');
  const out: Citation[] = [];
  for (const r of arr) {
    if (!r || typeof r !== 'object') continue;
    const rec = r as Record<string, unknown>;
    const url = String(rec.url ?? '');
    if (!isHttp(url)) continue;
    out.push({
      kind:    'web',
      title:   String(rec.title ?? rec.url ?? ''),
      snippet: String(rec.snippet ?? rec.description ?? ''),
      url,
      host:    hostOf(url),
    });
  }
  return out;
}

// workspace_corpus_search -> {hits:[{channel_name,author,posted_at,text}]}.
function workspaceCitations(tool: ToolStep): Citation[] {
  const arr = asArray(parseJson(tool.outputFull), 'hits');
  const out: Citation[] = [];
  for (const h of arr) {
    if (!h || typeof h !== 'object') continue;
    const rec = h as Record<string, unknown>;
    const text = String(rec.text ?? '').trim();
    if (!text) continue;
    const channel = String(rec.channel_name ?? '');
    const author = String(rec.author ?? '');
    const meta = [channel ? `#${channel}` : '', author].filter(Boolean).join(' · ');
    const c: Citation = {
      kind:    'workspace',
      title:   channel ? `#${channel}` : (author || 'Workspace message'),
      snippet: text,
    };
    if (meta) c.meta = meta;
    out.push(c);
  }
  return out;
}

// workspace_memory_search -> {hits}, workspace_memory_about_user -> {facts};
// FactEntry = {fact, fact_type, ...}.
function memoryCitations(tool: ToolStep): Citation[] {
  const data = parseJson(tool.outputFull);
  const arr = Array.isArray(data)
    ? data
    : (asArray(data, 'hits').length ? asArray(data, 'hits') : asArray(data, 'facts'));
  const out: Citation[] = [];
  for (const f of arr) {
    if (!f || typeof f !== 'object') continue;
    const rec = f as Record<string, unknown>;
    const text = String(rec.fact ?? '').trim();
    if (!text) continue;
    const type = String(rec.fact_type ?? '');
    const c: Citation = {
      kind:    'memory',
      title:   type || 'Memory',
      snippet: text,
    };
    if (type) c.meta = type;
    out.push(c);
  }
  return out;
}

const EXTRACTORS: Record<string, (t: ToolStep) => Citation[]> = {
  search_web:                  webCitations,
  workspace_corpus_search:     workspaceCitations,
  workspace_memory_search:     memoryCitations,
  workspace_memory_about_user: memoryCitations,
};

// Flatten every source-bearing tool result on the message into one ordered list,
// in tool-call order. [n] (1-based) maps to citationsFromTools(...)[n - 1].
export function citationsFromTools(tools: ToolStep[] | undefined): Citation[] {
  if (!tools || !tools.length) return [];
  const out: Citation[] = [];
  for (const tool of tools) {
    const extract = EXTRACTORS[baseName(tool.name)];
    if (extract) out.push(...extract(tool));
  }
  return out;
}

// The flattened citation index at which each tool's rows begin, aligned to the
// `tools` array by position (offsets[i] = count of citations from tools 0..i-1).
// ToolViewparts passes offsets[i] to each viewpart so a row's data-cite =
// offset + localIndex + 1 matches the [n] a CitationChip scrolls to. The
// per-tool counts come from the SAME extractors, so the two never drift.
export function citationOffsets(tools: ToolStep[] | undefined): number[] {
  const offsets: number[] = [];
  let running = 0;
  for (const tool of tools ?? []) {
    offsets.push(running);
    const extract = EXTRACTORS[baseName(tool.name)];
    if (extract) running += extract(tool).length;
  }
  return offsets;
}
